import React, { useState } from 'react'
import {
	Box,
	TextField,
	Typography,
	IconButton,
	Autocomplete,
	InputAdornment,
	useMediaQuery,
	useTheme
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { petTypes, dogBreedList, catBreedList } from '../../utils/breedAndPetTypeUtils'

// Single Pet Form Component
const PetForm = ({ pet, index, totalPets, onPetChange, errors = {} }) => {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	// Get breed list based on pet type
	const getBreedList = () => {
		if (pet.type === 'Dog') return dogBreedList
		if (pet.type === 'Cat') return catBreedList
		return []
	}

	const inputStyles = {
		'& .MuiOutlinedInput-root': {
			borderRadius: '6px',
			bgcolor: '#FFFFFF',
			'& fieldset': {
				borderColor: '#CBD5E0'
			},
			'&:hover fieldset': {
				borderColor: '#9F7AEA'
			},
			'&.Mui-focused fieldset': {
				borderColor: '#9F7AEA'
			}
		},
		'& .MuiInputBase-input': {
			fontFamily: 'Inter',
			fontWeight: 400,
			fontSize: '16px',
			lineHeight: 1.21,
			color: '#2D3748',
			padding: '12px 16px',
			'&::placeholder': {
				color: '#A0AEC0',
				opacity: 1
			}
		}
	}

	const labelStyles = {
		fontFamily: 'Inter',
		fontWeight: 400,
		fontSize: '12px',
		lineHeight: 1.21,
		letterSpacing: '-2.08%',
		color: '#2D3748',
		mb: '4px'
	}

	return (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			gap: '10px'
		}}>
			{/* Header with pet number indicator (only for multiple pets) */}
			{totalPets > 1 && (
				<Box sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: '22px'
				}}>
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: isMobile ? '20px' : '28px',
						lineHeight: 1.29,
						color: '#2D3748'
					}}>
						{index === 0 ? 'Add your first pet' : `Add pet ${index + 1}`}
					</Typography>
					<Box sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						padding: '0px 10px',
						bgcolor: '#EBEBEB',
						borderRadius: '8px'
					}}>
						<Typography sx={{
							fontFamily: 'Inter',
							fontWeight: 600,
							fontSize: '20px',
							lineHeight: 1.8,
							color: '#2D3748'
						}}>
							{index + 1} of {totalPets}
						</Typography>
					</Box>
				</Box>
			)}

			{/* Pet Form Card */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '16px',
				padding: '16px',
				bgcolor: '#FFFFFF',
				borderRadius: '12px',
				boxShadow: '0px 4px 4px -4px rgba(12, 12, 13, 0.05), 0px 16px 32px -4px rgba(12, 12, 13, 0.1)'
			}}>
				{/* Name and Pet Type Row */}
				<Box sx={{
					display: 'flex',
					flexDirection: isMobile ? 'column' : 'row',
					gap: '16px'
				}}>
					{/* Name */}
					<Box sx={{ flex: 1 }}>
						<Typography sx={labelStyles}>Name</Typography>
						<TextField
							fullWidth
							value={pet.name || ''}
							onChange={(e) => onPetChange(index, 'name', e.target.value)}
							placeholder="Pet name"
							error={!!errors.name}
							helperText={errors.name}
							sx={inputStyles}
						/>
					</Box>

					{/* Pet Type */}
					<Box sx={{ flex: 1 }}>
						<Typography sx={labelStyles}>Pet type</Typography>
						<Autocomplete
							fullWidth
							options={petTypes}
							value={pet.type || null}
							onChange={(e, newValue) => {
								// Reset breed when pet type changes - pass both updates at once
								const additionalUpdates = pet.type !== newValue ? { breed: null } : {}
								onPetChange(index, 'type', newValue, additionalUpdates)
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									placeholder="Select pet type"
									error={!!errors.type}
									helperText={errors.type}
									sx={inputStyles}
								/>
							)}
							popupIcon={<KeyboardArrowDownIcon sx={{ color: '#A0AEC0' }} />}
							sx={{
								'& .MuiAutocomplete-popupIndicator': {
									marginRight: '8px'
								},
								'& .MuiAutocomplete-inputRoot': {
									padding: '0 !important'
								},
								'& .MuiAutocomplete-input': {
									padding: '12px 16px !important'
								}
							}}
						/>
					</Box>
				</Box>

				{/* Breed */}
				<Box>
					<Typography sx={labelStyles}>Breed</Typography>
					<Autocomplete
						fullWidth
						freeSolo
						autoSelect
						options={getBreedList()}
						value={pet.breed || null}
						onChange={(e, newValue) => onPetChange(index, 'breed', newValue)}
						onInputChange={(e, newInputValue, reason) => {
							// Handle freeSolo input - update breed when user types
							if (reason === 'input') {
								onPetChange(index, 'breed', newInputValue)
							}
						}}
						disabled={!pet.type}
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder={pet.type ? "Choose pet breed" : "Select pet type first"}
								error={!!errors.breed}
								helperText={errors.breed}
								sx={inputStyles}
							/>
						)}
						popupIcon={<KeyboardArrowDownIcon sx={{ color: '#A0AEC0' }} />}
						sx={{
							'& .MuiAutocomplete-popupIndicator': {
								marginRight: '8px'
							},
							'& .MuiAutocomplete-inputRoot': {
								padding: '0 !important'
							},
							'& .MuiAutocomplete-input': {
								padding: '12px 16px !important'
							}
						}}
					/>
				</Box>

				{/* Weight */}
				<Box>
					<Typography sx={labelStyles}>Weight</Typography>
					<TextField
						fullWidth
						type="number"
						value={pet.weight || ''}
						onChange={(e) => onPetChange(index, 'weight', e.target.value)}
						placeholder="Enter weight"
						error={!!errors.weight}
						helperText={errors.weight}
						sx={inputStyles}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<Typography sx={{ color: '#A0AEC0', fontSize: '13px' }}>lbs</Typography>
								</InputAdornment>
							)
						}}
					/>
				</Box>
			</Box>
		</Box>
	)
}

// Main PetInfoStep Component
function PetInfoStep({
	pets,
	onPetsChange,
	currentPetIndex = 0,
	onCurrentPetIndexChange
}) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))
	const [errors, setErrors] = useState({})

	// Handle pet count change
	const handlePetCountChange = (delta) => {
		const newCount = Math.max(1, Math.min(10, pets.length + delta))

		if (newCount > pets.length) {
			// Add new empty pet
			onPetsChange([...pets, { name: '', type: null, breed: null, weight: '' }])
		} else if (newCount < pets.length) {
			// Remove last pet
			onPetsChange(pets.slice(0, newCount))
			// Adjust current index if needed
			if (currentPetIndex >= newCount) {
				onCurrentPetIndexChange?.(newCount - 1)
			}
		}
	}

	// Handle pet field change - supports updating multiple fields at once
	const handlePetChange = (index, field, value, additionalUpdates = {}) => {
		const newPets = [...pets]
		newPets[index] = { ...newPets[index], [field]: value, ...additionalUpdates }
		onPetsChange(newPets)

		// Clear error for this field
		if (errors[`${index}_${field}`]) {
			setErrors(prev => {
				const newErrors = { ...prev }
				delete newErrors[`${index}_${field}`]
				return newErrors
			})
		}
	}

	// Validate current pet
	const validateCurrentPet = () => {
		const pet = pets[currentPetIndex]
		const newErrors = {}

		if (!pet.name?.trim()) {
			newErrors[`${currentPetIndex}_name`] = 'Pet name is required'
		}
		if (!pet.type) {
			newErrors[`${currentPetIndex}_type`] = 'Pet type is required'
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	// Validate all pets
	const validateAllPets = () => {
		const newErrors = {}

		pets.forEach((pet, index) => {
			if (!pet.name?.trim()) {
				newErrors[`${index}_name`] = 'Pet name is required'
			}
			if (!pet.type) {
				newErrors[`${index}_type`] = 'Pet type is required'
			}
		})

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	// Get errors for specific pet
	const getPetErrors = (index) => {
		const petErrors = {}
		Object.keys(errors).forEach(key => {
			if (key.startsWith(`${index}_`)) {
				const field = key.replace(`${index}_`, '')
				petErrors[field] = errors[key]
			}
		})
		return petErrors
	}

	// Current pet data
	const currentPet = pets[currentPetIndex] || { name: '', type: null, breed: null, weight: '' }

	return (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			gap: '8px',
			width: '100%'
		}}>
			{/* Pet Count Section */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '8px'
			}}>
				<Typography sx={{
					fontFamily: 'Inter',
					fontWeight: 600,
					fontSize: isMobile ? '24px' : '28px',
					lineHeight: 1.29,
					color: '#2D3748'
				}}>
					How many pets?
				</Typography>

			</Box>

			{/* Pet Count Selector */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'flex-start',
				gap: '18px'
			}}>
				<Typography sx={{
					fontFamily: 'Inter',
					fontWeight: 400,
					fontSize: '13px',
					lineHeight: 1.21,
					color: '#4A5568'
				}}>
					Number of pets you're booking for
				</Typography>
				<Box sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					gap: '10px',
					width: '115px',
					height: '40px',
					bgcolor: '#FFFFFF',
					border: '1px solid #CBD5E0',
					borderRadius: '6px'
				}}>
					<IconButton
						onClick={() => handlePetCountChange(-1)}
						disabled={pets.length <= 1}
						size="small"
						sx={{
							color: pets.length <= 1 ? '#CBD5E0' : '#2D3748',
							p: 0
						}}
					>
						<RemoveIcon fontSize="small" />
					</IconButton>
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '13px',
						lineHeight: 1.21,
						color: '#000000',
						minWidth: '20px',
						textAlign: 'center'
					}}>
						{pets.length}
					</Typography>
					<IconButton
						onClick={() => handlePetCountChange(1)}
						disabled={pets.length >= 10}
						size="small"
						sx={{
							color: pets.length >= 10 ? '#CBD5E0' : '#2D3748',
							p: 0
						}}
					>
						<AddIcon fontSize="small" />
					</IconButton>
				</Box>
			</Box>

			{/* Divider */}
			<Box sx={{
				width: '100%',
				height: '1px',
				bgcolor: '#CBD5E0'
			}} />

			{/* Section Title for Single Pet */}
			{pets.length === 1 && (
				<Typography sx={{
					fontFamily: 'Inter',
					fontWeight: 600,
					fontSize: isMobile ? '24px' : '28px',
					lineHeight: 1.29,
					color: '#2D3748'
				}}>
					Add your first pet
				</Typography>
			)}

			{/* Pet Form */}
			<PetForm
				pet={currentPet}
				index={currentPetIndex}
				totalPets={pets.length}
				onPetChange={handlePetChange}
				errors={getPetErrors(currentPetIndex)}
			/>
		</Box>
	)
}

export default PetInfoStep

